<script lang="ts">
import { BAGGAGE_OPTIONS, STEP_ONE, STEP_TWO, REFUND_OPTIONS, STEP3_SECTION, STEP_THREE, TITLE_OPTIONS, ATTACH_TYPE } from '@/app/const';
import { ComplaintInfo } from '@/interface/Complaint.interface';
import { FormStep, FormStepItem } from '@/interface/Form.interface';
import CommonService from '@/services/commonService';
import { fileService, formService, masterService, ticketChannelService, userService } from '@/services/serviceProvider';
import { useI18n } from 'vue-i18n';

export default {
    setup() {
        const { t } = useI18n();
        const fileSv = fileService;
        const commonSv = new CommonService(t);
        const userSv = userService;

        return { fileSv, commonSv, userSv }
    },
    props: {
        modelValue: {
            type: Boolean,
            default: false,
        },
        header: String,
        confirmText: {
            type: String,
            default: 't-ok'
        },
        cancelText: {
            type: String,
            default: 't-cancel'
        },
        complaint: {
            type: Object,
            default: {}
        },
        haveStepFlight: Boolean,
        haveStepPassenger: Boolean,
        haveStepComplaint: Boolean,
        stepFlightNo: Number,
        stepPassengerNo: Number,
        stepComplaintNo: Number,
    },
    emits: ['update:modelValue', 'onCancel', 'onConfirm'],
    data() {
        return {
            request: this.complaint as ComplaintInfo,
            titleOptions: TITLE_OPTIONS,
            countryOptions: [] as any[],
            formStepComplaint: [] as FormStepItem[],
            stepOne: STEP_ONE,
            stepTwo: STEP_TWO,
            stepThree: STEP_THREE,
            baggageOptions: BAGGAGE_OPTIONS,
            refundOptions: REFUND_OPTIONS,
            lang: this.$i18n.locale,
        }
    },
    watch: {
        complaint: {
            handler(newVal, oldVal) {
                if (newVal) {
                    this.request = newVal;
                }
            }, deep: true
        },
        stepRequest() {
            return this.formStepComplaint.filter(f => f.name.startsWith(STEP3_SECTION.REQUEST) && f.name !== STEP3_SECTION.REQUEST);
        },
        '$i18n.locale': function (newVal, oldVal) {
            this.lang = newVal;
        }
    },
    computed: {
        dialogShow: {
            get() {
                return this.modelValue || false
            },
            set(newValue: boolean) {
                this.$emit('update:modelValue', newValue);
            }
        },
        ticketChannel(): string {
            if (this.request.flight)
                return ticketChannelService.ticketChannelString(this.request.flight.channel, this.lang);
            return '';
        },
        requestStepOne(): number[] {
            return this.request.form?.stepOne ?? [];
        },
        requestStepTwo(): number[] {
            return this.request.form?.stepTwo ?? [];
        },
        requestStepThree(): number[] {
            return this.request.form?.stepThree ?? [];
        },
        otherClaims() {
            return this.request.detail.claims?.find(o => o.claimsNo ===
                this.stepThree.RequestOther);
        }
    },
    async mounted() {
        await this.getFormStepComplaint();
        this.countryOptions = masterService.getCountryOptions(this.lang);
    },
    methods: {
        async getFormStepComplaint() {
            try {
                const res = await formService.getAllSteps();
                if (res && res.status == 200) {
                    const result = res.data as FormStep;
                    this.formStepComplaint = result.stepThree;
                }
            } catch (e: any) {
                throw e;
            }
        },
        confirm() {
            this.$emit('onConfirm')
        },
        cancel() {
            this.dialogShow = false;
            this.$emit('onCancel');
        },
        enableFlightSection(code: number) {
            return this.requestStepOne.includes(code);
        },
        enablePassengerSection(code: number) {
            return this.requestStepTwo.includes(code);
        },
        enableDetailSection(code: number) {
            return this.requestStepThree.includes(code);
        },
        anyAttachments(): boolean {
            return (this.request.detail.attachments !== undefined && this.request.detail.attachments.length > 0)
                || (this.request.detail.attachUploads !== undefined && this.request.detail.attachUploads.length > 0)
        },
        allAttachments(): any[] {
            let newUploads = this.request.detail.attachUploads as any[] ?? [];
            newUploads.forEach(up => { up.type = ATTACH_TYPE.NEW });
            let alreadySaved = this.request.detail.attachments as any[] ?? [];
            alreadySaved.forEach(saved => { saved.type = ATTACH_TYPE.SAVED });
            return newUploads.concat(alreadySaved);
        },
    }
}
</script>

<template>
    <v-dialog v-model="dialogShow" width="80%" height="90%" :close-on-back="false" persistent>
        <v-card class="summary-dialog big-dialog dialog-card pl-0">
            <v-toolbar density="compact" class="pl-6 py-0">
                <v-toolbar-title>{{ header }}</v-toolbar-title>
                <v-spacer></v-spacer>
                <v-toolbar-items class="d-flex align-center close-modal">
                    <v-btn class="h-fit-content pa-2" icon="mdi-close" @click="dialogShow = false"></v-btn>
                </v-toolbar-items>
            </v-toolbar>
            <v-divider class="mx-6"></v-divider>
            <v-card-text class="d-flex flex-column justify-center w-100 mb-2"
                style="overflow-y: auto; position: relative; overflow-x: hidden;">
                <div class="h-100 px-6 summary-content">
                    <div v-if="haveStepFlight" class="request-step-section d-flex-align-center mb-4 mx-2">
                        <v-avatar size="x-small" class="mr-2 active">
                            <span>{{ stepFlightNo }}</span>
                        </v-avatar>
                        {{ $t('t-complaint-flight-information') }}
                    </div>
                    <v-card v-if="request && request.flight && enableFlightSection(stepOne.Detail)"
                        class="inner-card border-1 mb-6 pb-2">
                        <div class="summary-info align-center mx-6 my-2 mt-5" style="gap: 18px;">
                            <div class="d-flex">
                                <span class="label mr-2">{{ $t('t-flight-number') }}</span>
                                <span class="value">{{ request.flight.flightNo }}</span>
                            </div>
                            <div class="splitter" style="height: inherit;"></div>
                            <div class="d-flex">
                                <span class="label mr-2">{{ $t('t-flight-date-fly') }}</span>
                                <span class="value">{{ request.flight.departureDateString }}</span>
                            </div>
                            <div class="splitter"></div>
                            <div class="d-flex">
                                <span class="label mr-2">{{ $t('t-complaint-ticket-channel') }}</span>
                                <span class="value">{{ ticketChannel }}</span>
                            </div>
                        </div>
                        <v-divider class="mx-5"></v-divider>
                        <div v-for="(booking, index) in request.flight.bookings">
                            <div class="summary-info booking align-center mx-6 my-2 mt-5" style="gap: 18px;">
                                <div class="d-flex align-center">
                                    <span class="label mr-2">{{ $t('t-flight-booking-no') }}
                                        {{ $t('t-flight-booking-no-eng') }}</span>
                                    <v-chip class="app-chip" color="primary">{{ booking.bookingNo }}</v-chip>
                                </div>
                                <div class="splitter" style="height: inherit;"></div>
                                <div class="d-flex">
                                    <span class="label mr-2">{{ $t('t-flight-booking-attach') }}</span>
                                    <span class="mr-2">{{ booking.attachments?.length }} {{ $t('t-file') }}</span>
                                    <v-chip v-for="attach in booking.attachments" class="app-chip mr-2" color="primary">
                                        {{ attach.fileName }}
                                    </v-chip>
                                </div>
                            </div>
                        </div>
                    </v-card>
                    <div v-if="haveStepPassenger" class="request-step-section d-flex-align-center mb-4 mx-2">
                        <v-avatar size="x-small" class="mr-2 active">
                            <span>{{ stepPassengerNo }}</span>
                        </v-avatar>
                        {{ $t('t-complaint-passenger-information') }}
                    </div>
                    <v-card v-if="request && request.passengers && enablePassengerSection(stepTwo.Detail)"
                        v-for="(passenger, index) in request.passengers" class="inner-card border-1 mb-4 pb-2">
                        <div class="summary-info align-center mx-6 my-2 mt-5" style="gap: 18px;">
                            <div class="d-flex align-center">
                                <span class="label mr-2">{{ $t('t-user-full-name') }}</span>
                                <span class="value">
                                    {{ $t(`t-${titleOptions.find(o => o.value === passenger.title)?.label}`) }}
                                    {{ passenger.firstName }}
                                    {{ passenger.middleName }}
                                    {{ passenger.lastName }}
                                </span>
                            </div>
                        </div>
                        <div class="summary-info align-center mx-6 my-2 mt-5" style="gap: 18px;">
                            <div class="d-flex align-center">
                                <span class="label mr-2">{{ $t('t-user-nationality') }}</span>
                                <span class="value">
                                    {{ countryOptions.find(o => o.code === passenger.nationality)?.name }}
                                </span>
                            </div>
                        </div>
                        <div class="summary-info align-center mx-6 my-2 mt-5" style="gap: 18px;">
                            <div class="d-flex align-center">
                                <span class="label mr-2">{{ $t('t-user-phone') }}</span>
                                <span class="value">{{ passenger.phoneNumber }}</span>
                            </div>
                            <div class="splitter" style="height: inherit;"></div>
                            <div class="d-flex">
                                <span class="label mr-2">{{ $t('t-user-email-full') }}</span>
                                <span class="value mr-2">{{ passenger.email }}</span>
                            </div>
                        </div>
                    </v-card>
                    <div v-if="haveStepComplaint" class="request-step-section d-flex-align-center mb-4 mx-2">
                        <v-avatar size="x-small" class="mr-2 active">
                            <span>{{ stepComplaintNo }}</span>
                        </v-avatar>
                        {{ $t('t-complaint-topic') }}
                    </div>
                    <v-card v-if="request && request.detail && haveStepComplaint" class="inner-card border-1 pb-2"
                        :class="userSv.isAsc() && request ? 'mb-6' : 'mb-0'">
                        <div v-if="enableDetailSection(stepThree.AirlineNotified)"
                            class="summary-info align-center mx-6 my-2 mt-5" style="gap: 18px;">
                            <div class="d-flex align-center">
                                <span class="label mr-4">
                                    {{ $t('t-complaint-airline-notify') }}
                                </span>
                                <span class="value">
                                    <v-icon v-if="request.detail.airlineNotify" icon="mdi mdi-check" color="success"
                                        size="small"></v-icon>
                                    <v-icon v-else-if="request.detail.airlineNotify === false" icon="mdi mdi-close"
                                        size="small" color="error"></v-icon>
                                    {{ request.detail.airlineNotify ? $t('t-complaint-notified') :
                                        request.detail.airlineNotify === false ? $t('t-complaint-not-notify-yet') : '' }}
                                </span>
                            </div>
                        </div>
                        <div v-if="enableDetailSection(stepThree.AirlineNotified) && request.detail.airlineNotify"
                            class="summary-info align-center mx-6 my-2 mt-5" style="gap: 18px;">
                            <div class="d-flex align-center">
                                <span class="label mr-2">{{ $t('t-complaint-airline-notify-date-time-short') }}</span>
                                <span class="value">
                                    {{ request.detail.airlineNotifyDateString }}
                                    {{ `${$t('t-time')} ${request.detail.airlineNotifyTime} ${$t('t-time-unit')}` }}
                                </span>
                            </div>
                        </div>
                        <div v-if="enableDetailSection(stepThree.DelayHour)"
                            class="summary-info align-center mx-6 my-2 mt-5" style="gap: 18px;">
                            <div class="d-flex align-center">
                                <span class="label mr-2">{{ $t('t-complaint-airline-hours-delay') }}</span>
                                <span class="value" v-if="request.flight?.flightInfo?.delayString">
                                    <span class="mr-2">
                                        {{ commonSv.timeString(request.flight.flightInfo.delayString) }}
                                    </span>
                                </span>
                            </div>
                        </div>
                        <div v-if="enableDetailSection(stepThree.Baggage)"
                            class="summary-info align-center mx-6 my-2 mt-5" style="gap: 18px;">
                            <div class="d-flex align-center">
                                <span class="label mr-2">{{ $t('t-complaint-request-reason') }}</span>
                                <span class="value">
                                    {{ $t(`t-${baggageOptions.find(o => o.value ===
                                        request.detail.baggageComplaint)?.label}`) }}
                                </span>
                            </div>
                        </div>
                        <div v-if="enableDetailSection(stepThree.NeedRefundForService)"
                            class="summary-info align-center mx-6 my-2 mt-5" style="gap: 18px; max-width: 100%;">
                            <div class="d-flex align-center">
                                <span class="label mr-2">{{ $t('t-complaint-request-reason') }}</span>
                                <span class="value">
                                    {{ $t(`t-${refundOptions.find(o => o.value ===
                                        request.detail.refundService)?.label}`) }}
                                </span>
                            </div>
                        </div>
                        <div v-if="enableDetailSection(stepThree.NeedDetail)"
                            class="summary-info align-center mx-6 my-2 mt-5" style="gap: 18px;">
                            <div class="d-flex flex-column align-start">
                                <span class="label mr-2">{{ $t('t-complaint-remark-short') }}</span>
                                <span class="value" style="text-wrap: wrap;">{{ request.detail.detail ?? '-' }}</span>
                            </div>
                        </div>
                        <div v-if="enableDetailSection(stepThree.NotifiedAirline)"
                            class="summary-info align-center mx-6 my-2 mt-5" style="gap: 18px;">
                            <div class="d-flex align-center">
                                <span class="label mr-4">{{ $t('t-card-reported-to-airline') }}</span>
                                <span class="value">
                                    <v-icon v-if="request.detail.notifyAirline" icon="mdi mdi-check" color="success"
                                        size="small"></v-icon>
                                    <v-icon v-else-if="request.detail.notifyAirline === false" icon="mdi mdi-close"
                                        size="small" color="error"></v-icon>
                                    {{ request.detail.notifyAirline ? $t('t-yes') : request.detail.notifyAirline ===
                                        false ? $t('t-no') : '' }}
                                </span>
                            </div>
                        </div>
                        <div v-if="enableDetailSection(stepThree.NotifiedAirline) && request.detail.notifyAirline"
                            class="summary-info align-center mx-6 my-2 mt-5" style="gap: 18px;">
                            <div class="d-flex align-center">
                                <span class="label mr-2">{{ $t('t-complaint-notify-number-short') }}</span>
                                <span class="value">{{ request.detail.notifyNumber }}</span>
                            </div>
                        </div>
                        <div v-if="enableDetailSection(stepThree.Attachment)"
                            class="summary-info align-center mx-6 my-2 mt-5" style="gap: 18px;">
                            <div class="d-flex align-center">
                                <span class="label mr-2">{{ $t('t-card-attachment-other') }}</span>
                                <span v-if="anyAttachments()" class="value mr-2">
                                    {{ allAttachments().length }} {{ $t('t-file') }}
                                </span>
                                <span v-else>-</span>
                                <v-chip v-for="attach in allAttachments()" class="app-chip mr-2" color="primary">
                                    <p v-if="fileSv.isNewAttachment(attach.type)">{{ attach.name }}</p>
                                    <p v-if="fileSv.isSavedAttachment(attach.type)">{{ attach.fileName }}</p>
                                </v-chip>
                            </div>
                        </div>
                        <div v-if="enableDetailSection(stepThree.Request)"
                            class="summary-info align-center mx-6 my-2 mt-5" style="gap: 18px;">
                            <div class="d-flex flex-column w-100">
                                <div class="d-flex align-center mb-5">
                                    <span class="label mr-2">{{ $t('t-complaint-request') }}</span>
                                    <v-chip v-for="claim in request.detail.claims" variant="flat" color="success"
                                        class="app-chip gradient mr-2">
                                        <div v-if="formStepComplaint.find(o => o.id === claim.claimsNo)" class="mr-1">
                                            {{ $t(`t-${formStepComplaint.find(o => o.id === claim.claimsNo)?.name}`) }}
                                        </div>
                                    </v-chip>
                                </div>
                                <div class="w-100 d-flex summary-claims flex-column"
                                    v-if="request.detail.claims?.map(o => o.claimsNo).includes(stepThree.RequestOther)">
                                    <span class="label mr-2">{{ $t('t-card-list-other-remark') }}</span>
                                    <v-textarea v-if="otherClaims?.remark?.toString()"
                                        :value="otherClaims.remark.toString()" class="w-100 hide-float-label" readonly
                                        variant="plain" no-resize auto-grow
                                        :rows="otherClaims.remark.toString().length > 200 ? 8 : 3">
                                    </v-textarea>
                                    <!-- <v-card class="inner-card">
                                        <v-card-text style="word-wrap: break-word;">
                                            <div class="d-flex flex-column">
                                                <span class="label mr-2">{{ $t('t-card-list-other-remark') }}</span>
                                                {{ request.detail.claims.find(o => o.claimsNo ===
                                                    stepThree.RequestOther)?.remark }}
                                            </div>
                                        </v-card-text>
                                    </v-card> -->
                                </div>
                            </div>
                        </div>
                    </v-card>
                    <v-card v-if="userSv.isAsc() && request" class="inner-card border-1 mb-4 pb-2">
                        <div class="summary-info align-center mx-6 my-2 mt-5" style="gap: 18px;">
                            <div class="d-flex align-center">
                                <span class="label mr-2">{{ $t('t-complaint-doc-from') }}</span>
                                <span class="value">
                                    {{ request.remark ?? '-' }}
                                </span>
                            </div>
                        </div>
                        <div class="summary-info align-center mx-6 my-2 mt-5" style="gap: 18px;">
                            <div class="d-flex align-start flex-column">
                                <span class="label mr-2">{{ $t('t-complaint-complaintant-info') }}</span>
                                <div>
                                    <div class="summary-info align-center mx-6 my-2 mt-5" style="gap: 18px;">
                                        <div class="d-flex align-center">
                                            <span class="label mr-2">{{ $t('t-user-full-name') }}</span>
                                            <span class="value">
                                                {{ $t(`t-${titleOptions.find(o => o.value ===
                                                    request.complainant.title)?.label}`) }}
                                                {{ request.complainant.firstName }}
                                                {{ request.complainant.middleName }}
                                                {{ request.complainant.lastName }}
                                            </span>
                                        </div>
                                    </div>
                                    <div class="summary-info align-center mx-6 my-2 mt-5" style="gap: 18px;">
                                        <div class="d-flex align-center">
                                            <span class="label mr-2">{{ $t('t-user-phone') }}</span>
                                            <span class="value">{{ request.complainant.phoneNumber ?? '-' }}</span>
                                        </div>
                                        <div class="splitter" style="height: inherit;"></div>
                                        <div class="d-flex">
                                            <span class="label mr-2">{{ $t('t-user-email-full') }}</span>
                                            <span class="value mr-2">{{ request.complainant.email ?? '-' }}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </v-card>
                    <div style="height: 5px;"></div>
                </div>
            </v-card-text>
            <v-card-actions>
                <v-container class="mx-0">
                    <div class="d-flex justify-end">
                        <v-btn @click="cancel" variant="outlined" color="primary" size="large">
                            {{ $t('t-complaint-back-to-edit') }}
                        </v-btn>
                        <v-btn @click="confirm" variant="flat" color="primary" size="large" min-width="120">
                            {{ $t('t-complaint-submit') }}
                        </v-btn>
                    </div>
                </v-container>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>
